import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import {
  useCreateWorkspaceMutationApi,
  useUpdateWorkspace,
} from '@aibees/shared/workspace';
import { Button, Stack, Typography } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { FormDialog, FormDialogProps } from '../form-dialog';
import { RHFTextField } from '../hook-form';
import { config } from './config';
import * as Styled from './styles';
import type {
  CreateWorkspaceFormData as FormData,
  CreateWorkspaceProps as Props,
} from './types';

declare global {
  interface Window {
    tolt_referral?: string;
  }
}

export const CreateWorkspace = memo(({ className }: Props) => {
  const api = useCreateWorkspaceMutationApi();
  const notify = useNotify();
  const updateWorkspace = useUpdateWorkspace();

  const handleCreateWorkspace = useCallback<FormDialogProps['onSubmit']>(
    (data: FormData, e) => {
      e.startProgress();
      const toltReferral = window.tolt_referral;
      api
        .mutateAsync({
          ...data,
          ...(toltReferral ? { referral: toltReferral } : {}),
        })
        .then((workspace) => {
          updateWorkspace(workspace);
          notify.info('Workspace created successfully');
          e.toggle();
        })
        .catch(e.startProgress);
    },
    [api, notify, updateWorkspace]
  );

  const children = useMemo(
    () => (
      <>
        <Stack flexDirection="column">
          <Typography color="primary" variant="h5">
            You have no workspaces yet
          </Typography>

          <Typography color="text" variant="body1" textAlign="center">
            would you like to create a new one?
          </Typography>
        </Stack>

        <FormDialog
          uncontrolledElement={
            <Button color="primary" size="large" variant="text">
              Create a new workspace
            </Button>
          }
          {...config}
          onSubmit={handleCreateWorkspace}
        >
          <RHFTextField name="name" label="Workspace Name" />
        </FormDialog>
      </>
    ),
    [handleCreateWorkspace]
  );

  return (
    <Styled.Wrapper
      className={classnames('CreateWorkspace', className)}
      flexDirection="column"
      gap={4}
    >
      {children}
    </Styled.Wrapper>
  );
});

CreateWorkspace.displayName = 'CreateWorkspace';
